import { combineLatest, isObservable, map, Observable, of, pipe, shareReplay, switchMap, take } from 'rxjs';

const SYM_IS_PREVENTABLE_EVENT = Symbol('IsPreventableEvent');

export type ReactiveEvent<Payload = undefined, Reaction = void, K extends PropertyKey = 'react'> = {
  [Key in K]: (reaction?: Reaction) => void;
} & {
  data?: Payload;
};

/**
 * A PreventableEvent is an event that can be prevented from its default behavior.
 * Call `preventDefault` to prevent the default behavior. If you want to continue after an asycn operation provide an Observable to `preventDefault` that emits exactly one value.
 * The emitter can then decide to proceed based on the values. Should not proceed if at least one preventDefault call did not provide a value.
 * preventDefault MUST BE CALLED SYNCRONOUSLY! Otherwise the event will be considered as not prevented.
 */
export type PreventableEvent<Payload = unknown, Reaction = unknown> = ReactiveEvent<Payload, Observable<Reaction>, 'preventDefault'> & {
  [SYM_IS_PREVENTABLE_EVENT]: true;
};

export function isPreventableEvent(event: unknown): event is PreventableEvent {
  return (
    event !== null &&
    typeof event === 'object' &&
    SYM_IS_PREVENTABLE_EVENT in event &&
    (event as Record<PropertyKey, unknown>)[SYM_IS_PREVENTABLE_EVENT] === true
  );
}

export function reactiveEvent<R = void, D = undefined>(payload?: D): { event: ReactiveEvent<D, R>; reactions: () => Array<R> } {
  const _reactions: Array<R> = [];
  return {
    event: { react: (reaction: R) => _reactions.push(reaction), data: payload },
    reactions: () => _reactions,
  };
}

/**
 * Creates a preventableEvent.
 * It contains a preventDefault method that allows the consumer to pass an observable.
 * preventDefault MUST be called synchronously! Otherwise the event will be considered as not prevented.
 * If no observable is passed, the event will be considered as prevented.
 * If at least one observable returns true, the event will be considered as prevented.
 * The emitter can than use the following methods to decide how to proceed:
 * - proceed: Will execute the provided function if the event is NOT prevented.
 * - prevent: Will execute the provided function if the event is prevented.
 * - finalize: Will execute the provided function regardless of prevention. Used to evaluate reactions.
 *
 * How to use:
 * Emitter:
 * ````
 * const { event, prevent, proceed, finalize } = preventableEvent({ payload: 'data' });
 * eventEmitter.next(event); // Emit the event so consumers can react synchronously. So at the next line you know if the event reacted to.
 * proceed(() => console.log('proceeding')); // Will log 'proceed' if the event was not prevented.
 * prevent(() => console.log('prevented')); // Will log 'prevent' if the event was prevented.
 * finalize((reactions) => console.log('finalizing', reactions)); // Will log 'finalizing' with an array of reactions regardless of prevention.
 * ````
 **/
export function preventableEvent<D = undefined, R = unknown>(
  payload?: D,
): {
  event: PreventableEvent<D, R>;
  shouldPrevent: () => Observable<boolean>;
  reactions: () => Observable<(R | boolean)[]>;
  proceed: (fn: () => void) => void;
  prevent: (fn: () => void) => void;
  finalize: (fn: (reactions: (boolean | R)[]) => void) => void;
  observe: () => Observable<{ prevented: boolean; reactions: (R | boolean)[] }>;
} {
  const _reactions: Array<Observable<R> | true> = [];
  let _observeReactions: Observable<(R | true)[]> | undefined = undefined;
  function reactions() {
    if (!_observeReactions) {
      _observeReactions = _reactions.length
        ? combineLatest(_reactions.map(r => (isObservable(r) ? r : of(r)))).pipe(take(1), shareReplay(1))
        : of([] as (R | true)[]).pipe(shareReplay(1));
    }
    return _observeReactions;
  }
  function shouldPrevent() {
    return reactions().pipe(map(r => r.some(e => e === true)));
  }
  return {
    event: {
      [SYM_IS_PREVENTABLE_EVENT]: true,
      preventDefault: (reaction?: Observable<R>) => _reactions.push(reaction ?? true),
      data: payload,
    },
    reactions,
    shouldPrevent,
    proceed: (fn: () => void) => shouldPrevent().subscribe(prevent => prevent || fn()),
    prevent: (fn: () => void) => shouldPrevent().subscribe(prevent => prevent && fn()),
    finalize: (fn: (reactions: (boolean | R)[]) => void) => reactions().subscribe(r => fn(r)),
    observe: () => combineLatest([shouldPrevent(), reactions()]).pipe(map(([prevented, reactions]) => ({ prevented, reactions }))),
  };
}

export type IfNotPreventedConfig = {
  onPrevented?: 'filter' | 'throw'; // What to do if the event is prevented filter out or throw an error
  invert?: boolean; // Invert the check, so it only passes if the event is prevented
};

/** Given a preventable event, will filter the observable if the event is prevented */
export function ifNotPrevented<E>(event: ReturnType<typeof preventableEvent<E>>, config?: IfNotPreventedConfig) {
  config = {
    onPrevented: 'filter',
    invert: false,
    ...config,
  };
  return pipe(
    switchMap((v: unknown) => {
      return new Observable(sub => {
        if (config.invert) {
          event.prevent(() => sub.next(v));
          if (config.onPrevented === 'throw') {
            throw event;
          }
        } else {
          event.proceed(() => sub.next(v));
          if (config.onPrevented === 'throw') {
            throw event;
          }
        }
      });
    }),
  );
}
